import { useState } from "react";
import type { HookConfig, HookEvent, HookType } from "@cowork/shared";
import { cn } from "../../lib/utils.js";

const HOOK_EVENTS: HookEvent[] = [
  "PreToolUse",
  "PostToolUse",
  "SessionStart",
  "SessionStop",
];

function HookCard({
  hook,
  onToggle,
  onDelete,
}: {
  hook: HookConfig;
  onToggle: () => void;
  onDelete: () => void;
}) {
  return (
    <div className="rounded-xl border border-[var(--border)] bg-[var(--bg-secondary)] p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-[var(--text-primary)]">
            {hook.name}
          </span>
          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[var(--bg-tertiary)] text-[var(--text-secondary)]">
            {hook.event}
          </span>
          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[var(--bg-tertiary)] text-[var(--text-secondary)]">
            {hook.type}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onToggle}
            className={cn(
              "relative inline-flex h-5 w-9 rounded-full transition-colors",
              hook.enabled ? "bg-[var(--accent)]" : "bg-[var(--bg-tertiary)]",
            )}
          >
            <span
              className={cn(
                "inline-block h-4 w-4 rounded-full bg-white shadow transition-transform m-0.5",
                hook.enabled ? "translate-x-4" : "translate-x-0",
              )}
            />
          </button>
          <button
            onClick={onDelete}
            className="text-[var(--text-secondary)] hover:text-red-400 transition-colors text-xs"
          >
            ✕
          </button>
        </div>
      </div>

      {hook.description && (
        <p className="text-xs text-[var(--text-secondary)] mb-2">
          {hook.description}
        </p>
      )}

      {hook.command && (
        <code className="text-xs text-[var(--accent)] font-mono bg-[var(--bg-tertiary)] px-2 py-1 rounded">
          $ {hook.command}
        </code>
      )}
      {hook.url && (
        <code className="text-xs text-[var(--accent)] font-mono bg-[var(--bg-tertiary)] px-2 py-1 rounded">
          {hook.url}
        </code>
      )}
    </div>
  );
}

interface NewHookFormData {
  name: string;
  event: HookEvent;
  type: HookType;
  command: string;
  url: string;
  description: string;
}

const DEFAULT_FORM: NewHookFormData = {
  name: "",
  event: "PreToolUse",
  type: "command",
  command: "",
  url: "",
  description: "",
};

export function HooksConfigurator() {
  const [hooks, setHooks] = useState<HookConfig[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<NewHookFormData>(DEFAULT_FORM);

  const addHook = () => {
    if (!form.name) return;

    const newHook: HookConfig = {
      id: crypto.randomUUID(),
      name: form.name,
      event: form.event,
      type: form.type,
      ...(form.description && { description: form.description }),
      enabled: true,
      ...(form.type === "command" && form.command && { command: form.command }),
      ...(form.type === "http" && form.url && { url: form.url }),
    };

    setHooks((prev) => [...prev, newHook]);
    setForm(DEFAULT_FORM);
    setShowForm(false);
  };

  const toggleHook = (id: string) => {
    setHooks((prev) =>
      prev.map((h) => (h.id === id ? { ...h, enabled: !h.enabled } : h)),
    );
  };

  const deleteHook = (id: string) => {
    setHooks((prev) => prev.filter((h) => h.id !== id));
  };

  return (
    <div className="flex flex-col h-full overflow-y-auto">
      <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border)]">
        <div>
          <h2 className="text-base font-semibold" style={{ color: "var(--text-primary)" }}>Hooks</h2>
          <p className="text-xs text-[var(--text-secondary)] mt-0.5">
            Run commands or call webhooks on tool events
          </p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="text-sm px-3 py-1.5 rounded-lg bg-[var(--accent)] text-white hover:bg-[var(--accent-hover)] transition-colors"
        >
          + Add Hook
        </button>
      </div>

      <div className="flex-1 p-6 space-y-3">
        {hooks.length === 0 && !showForm ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none" className="mb-3" style={{ color: "var(--text-tertiary)" }}>
              <path d="M16 2v16a6 6 0 1 1-6-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" fill="none" />
            </svg>
            <h3 className="text-sm font-medium" style={{ color: "var(--text-primary)" }}>No hooks configured</h3>
            <p className="text-xs text-[var(--text-secondary)] mt-1 max-w-xs">
              Hooks let you run shell commands or call HTTP endpoints
              before/after tool use and session events.
            </p>
          </div>
        ) : (
          hooks.map((h) => (
            <HookCard
              key={h.id}
              hook={h}
              onToggle={() => toggleHook(h.id)}
              onDelete={() => deleteHook(h.id)}
            />
          ))
        )}

        {showForm && (
          <div className="rounded-xl border border-[var(--accent)]/40 bg-[var(--bg-secondary)] p-4 space-y-3">
            <h3 className="text-sm font-medium">New Hook</h3>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] text-[var(--text-secondary)] uppercase mb-1 block">
                  Name
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="my-hook"
                  className="w-full text-xs rounded-lg border border-[var(--border)] bg-[var(--bg-tertiary)] text-[var(--text-primary)] px-2 py-1.5 outline-none"
                />
              </div>

              <div>
                <label className="text-[10px] text-[var(--text-secondary)] uppercase mb-1 block">
                  Event
                </label>
                <select
                  value={form.event}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      event: e.target.value as HookEvent,
                    }))
                  }
                  className="w-full text-xs rounded-lg border border-[var(--border)] bg-[var(--bg-tertiary)] text-[var(--text-primary)] px-2 py-1.5 outline-none"
                >
                  {HOOK_EVENTS.map((ev) => (
                    <option key={ev} value={ev}>
                      {ev}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="text-[10px] text-[var(--text-secondary)] uppercase mb-1 block">
                Type
              </label>
              <div className="flex gap-2">
                {(["command", "http"] as HookType[]).map((t) => (
                  <button
                    key={t}
                    onClick={() => setForm((f) => ({ ...f, type: t }))}
                    className={cn(
                      "text-xs px-3 py-1 rounded-lg border transition-colors",
                      form.type === t
                        ? "border-[var(--accent)] bg-[var(--accent)]/10 text-[var(--accent)]"
                        : "border-[var(--border)] text-[var(--text-secondary)]",
                    )}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>

            {form.type === "command" ? (
              <div>
                <label className="text-[10px] text-[var(--text-secondary)] uppercase mb-1 block">
                  Command
                </label>
                <input
                  type="text"
                  value={form.command}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, command: e.target.value }))
                  }
                  placeholder="echo $COWORK_CONTEXT"
                  className="w-full text-xs rounded-lg border border-[var(--border)] bg-[var(--bg-tertiary)] text-[var(--text-primary)] px-2 py-1.5 outline-none font-mono"
                />
              </div>
            ) : (
              <div>
                <label className="text-[10px] text-[var(--text-secondary)] uppercase mb-1 block">
                  URL
                </label>
                <input
                  type="text"
                  value={form.url}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, url: e.target.value }))
                  }
                  placeholder="https://hooks.example.com/cowork"
                  className="w-full text-xs rounded-lg border border-[var(--border)] bg-[var(--bg-tertiary)] text-[var(--text-primary)] px-2 py-1.5 outline-none"
                />
              </div>
            )}

            <div className="flex gap-2 justify-end">
              <button
                onClick={() => {
                  setShowForm(false);
                  setForm(DEFAULT_FORM);
                }}
                className="text-xs px-3 py-1.5 rounded-lg text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={addHook}
                disabled={!form.name}
                className="text-xs px-3 py-1.5 rounded-lg bg-[var(--accent)] text-white hover:bg-[var(--accent-hover)] disabled:opacity-50 transition-colors"
              >
                Add Hook
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
